import React from 'react';
import { usePermission, usePermissions } from './hooks';

/**
 * Higher-order component to protect components based on permissions
 * @param {React.Component} Component - The component to protect
 * @param {string|string[]} requiredPermissions - Permission(s) required to render the component
 * @param {React.Component} FallbackComponent - Component to render if permission check fails (optional)
 * 
 * @example
 * // Single permission
 * const ProtectedButton = withPermission(CreateButton, 'attendance.create attendance');
 * 
 * // Multiple permissions (ALL required)
 * const ProtectedPanel = withPermission(AdminPanel, ['admin.view', 'admin.manage']);
 */
export const withPermission = (Component, requiredPermissions, FallbackComponent = null) => {
    return (props) => {
        const isSinglePermission = typeof requiredPermissions === 'string';
        const singlePermission = usePermission(isSinglePermission ? requiredPermissions : '');
        const multiplePermissions = usePermissions(isSinglePermission ? [] : requiredPermissions);
        
        const hasPermission = isSinglePermission ? singlePermission : multiplePermissions;

        if (!hasPermission) {
            return FallbackComponent ? <FallbackComponent {...props} /> : null;
        }

        return <Component {...props} />;
    };
};

/**
 * Component to conditionally render children based on permissions
 * @param {string|string[]} requires - Permission(s) required to render children
 * @param {React.ReactNode} children - Content to render if permission check passes
 * @param {React.ReactNode} fallback - Content to render if permission check fails (optional)
 * 
 * @example
 * <PermissionGuard requires="attendance.create attendance">
 *     <button>Create Attendance</button>
 * </PermissionGuard>
 * 
 * // Multiple permissions
 * <PermissionGuard requires={['admin.view', 'admin.manage']} fallback={<p>Access Denied</p>}>
 *     <AdminPanel />
 * </PermissionGuard>
 */
export const PermissionGuard = ({ requires, children, fallback = null }) => {
    const isSinglePermission = typeof requires === 'string';
    const singlePermission = usePermission(isSinglePermission ? requires : '');
    const multiplePermissions = usePermissions(isSinglePermission ? [] : requires);
    
    const hasPermission = isSinglePermission ? singlePermission : multiplePermissions;

    return hasPermission ? children : fallback;
};
